import { ApiClient } from '@utils/apiClient';
import { ApiResult, handleApiResult } from '@utils/apiErrorHandler';
import { CompanyInfoInput, FaqEntry, FaqEntryInput } from '../types';

export class FaqApiError extends Error {
  fieldErrors: Record<string, string>;

  constructor(message: string, fieldErrors: Record<string, string> = {}) {
    super(message);
    this.name = 'FaqApiError';
    this.fieldErrors = fieldErrors;
  }
}

// Shared with the unanswered questions, whose answer form shows the FAQ field errors
export const unwrap = <T>(result: ApiResult<T>, fallback: string): T | undefined => {
  if (result.success) return result.data;
  const errors = result.errors;
  throw new FaqApiError(
    errors?.generalError || errors?.nonFieldErrors?.[0] || fallback,
    errors?.fieldErrors
  );
};

const BASE_PATH = '/knowledge-base/faq';

export const faqService = {
  list: async (knowledgeBaseId: string, search: string): Promise<FaqEntry[]> => {
    const params = new URLSearchParams({ knowledge_base: knowledgeBaseId });
    if (search) params.set('search', search);
    const response = await ApiClient.get(`${BASE_PATH}/?${params.toString()}`);
    return (
      unwrap<FaqEntry[]>(
        await handleApiResult(response, 'Failed to load FAQ entries'),
        'Failed to load FAQ entries'
      ) ?? []
    );
  },
  create: async (knowledgeBaseId: string, data: FaqEntryInput): Promise<FaqEntry> => {
    const response = await ApiClient.post(`${BASE_PATH}/`, {
      ...data,
      knowledge_base: knowledgeBaseId,
    });
    return unwrap<FaqEntry>(
      await handleApiResult(response, 'Failed to create FAQ entry'),
      'Failed to create FAQ entry'
    ) as FaqEntry;
  },
  update: async (id: string, data: FaqEntryInput): Promise<FaqEntry> => {
    const response = await ApiClient.patch(`${BASE_PATH}/${id}/`, data);
    return unwrap<FaqEntry>(
      await handleApiResult(response, 'Failed to update FAQ entry'),
      'Failed to update FAQ entry'
    ) as FaqEntry;
  },
  remove: async (id: string): Promise<void> => {
    const response = await ApiClient.delete(`${BASE_PATH}/${id}/`);
    unwrap<void>(
      await handleApiResult(response, 'Failed to delete FAQ entry'),
      'Failed to delete FAQ entry'
    );
  },
  // Restarts the embedding, e.g. after a failure or a lost task
  process: async (id: string): Promise<FaqEntry> => {
    const response = await ApiClient.post(`${BASE_PATH}/${id}/process/`, {});
    return unwrap<FaqEntry>(
      await handleApiResult(response, 'Failed to restart processing'),
      'Failed to restart processing'
    ) as FaqEntry;
  },
};

const companyInfoPath = (knowledgeBaseId: string) =>
  `/knowledge-base/${knowledgeBaseId}/company-info/`;

export const companyInfoService = {
  get: async (knowledgeBaseId: string): Promise<FaqEntry[]> => {
    const response = await ApiClient.get(companyInfoPath(knowledgeBaseId));
    return (
      unwrap<FaqEntry[]>(
        await handleApiResult(response, 'Failed to load company info'),
        'Failed to load company info'
      ) ?? []
    );
  },
  // Answers that did not change are not processed again
  save: async (knowledgeBaseId: string, data: CompanyInfoInput): Promise<FaqEntry[]> => {
    const response = await ApiClient.put(companyInfoPath(knowledgeBaseId), data);
    return (
      unwrap<FaqEntry[]>(
        await handleApiResult(response, 'Failed to save company info'),
        'Failed to save company info'
      ) ?? []
    );
  },
};
